import Honeybadger from '@honeybadger-io/js'
import type { App, Plugin } from 'vue'

export const INSTALL_FAILED_NAME = 'HoneybadgerInstallFailed'

/**
 * Finds the first stack frame that does not come from our own bundle, e.g. the
 * `user-script:151` frame of a Safari extension that monkey-patched a global.
 * @param error - The error thrown while installing the plugin.
 * @returns The trimmed stack line, or undefined when every frame is ours.
 */
export function findOffendingFrame(error: unknown): string | undefined {
  const stack = error instanceof Error ? error.stack || '' : ''
  const origin = window.location.origin
  const stackFramePattern = /:\d+(:\d+)?\)?$/

  return stack
    .split('\n')
    .map((line) => line.trim())
    .find((line) => stackFramePattern.test(line) && !line.includes(origin))
}

/**
 * Installs the Honeybadger Vue plugin without letting a failure block boot.
 * A browser extension that breaks a global can make a plugin's `load()` throw
 * inside `Honeybadger.configure()`. The config is already applied by then, so
 * the singleton can still report, and the app must still mount.
 * @param app - The Vue app being booted.
 * @param plugin - The Honeybadger Vue plugin.
 * @param config - The Honeybadger configuration.
 */
export function installHoneybadger(
  app: App,
  plugin: Plugin,
  config: Partial<Parameters<typeof Honeybadger.configure>[0]>
): void {
  try {
    app.use(plugin, config)
  } catch (error) {
    app.$honeybadger = Honeybadger
    app.config.globalProperties.$honeybadger = Honeybadger

    try {
      Honeybadger.notify(error as Error, {
        name: INSTALL_FAILED_NAME,
        context: {
          originalMessage: error instanceof Error ? error.message : String(error),
          offendingFrame: findOffendingFrame(error)
        }
      })
    } catch (notifyError) {
      console.error(error, notifyError)
    }
  }
}
